import React, { useEffect, useState } from 'react';
import { eventsAPI } from '../services/api';
import InteractiveGallery from '../components/InteractiveGallery';
import './Home.css';
import './Events.css';

interface Event {
  id: number;
  title: string;
  date: string;
  time: string;
  description: string;
  image?: string;
  imageUrl?: string;
  images?: string[];
  isRecurring?: boolean;
  recurringType?: 'NONE' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
  isGenerated?: boolean;
}

const Home: React.FC = () => {
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(true);

  const [upcomingEvents, setUpcomingEvents] = useState<any[]>([]);

  const fetchUpcomingEvents = async () => {
    try {
      setEventsLoading(true);
      const response = await eventsAPI.getAll();
      const events = response.data || [];

      // Get next 3 upcoming events OR recent past events (last 7 days)
      const now = new Date();
      now.setHours(0, 0, 0, 0); // Set to start of day for fair comparison

      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const upcoming = events
        .filter((event: any) => {
          const eventDate = new Date(event.date);
          eventDate.setHours(0, 0, 0, 0);
          // Show if upcoming OR within last 7 days
          return eventDate >= sevenDaysAgo;
        })
        .sort((a: any, b: any) => {
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        })
        .slice(0, 3); // Show max 3 events

      setUpcomingEvents(upcoming);
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
    } finally {
      setEventsLoading(false);
    }
  };

  const formatEventDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatFullEventDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleEventClick = (event: Event, e: React.MouseEvent) => {
    e.preventDefault();
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const closeEventModal = () => {
    setSelectedEvent(null);
    setShowEventModal(false);
  };

  useEffect(() => {
    // Fetch upcoming events
    fetchUpcomingEvents();

    // Smooth scroll offset for navigation
    const handleHashScroll = () => {
      if (window.location.hash) {
        const element = document.getElementById(window.location.hash.slice(1));
        if (element) {
          const headerHeight = 100; // Approximate header height
          const elementPosition = element.offsetTop - headerHeight - 20;
          window.scrollTo({
            top: elementPosition,
            behavior: 'smooth'
          });
        }
      }
    };

    // Handle initial load
    setTimeout(handleHashScroll, 100);

    // Handle hash changes
    window.addEventListener('hashchange', handleHashScroll);
    return () => window.removeEventListener('hashchange', handleHashScroll);
  }, []);

  return (
    <div className="home">
      {/* Hero Section with Upcoming Events */}
      <section
        className="hero hero-with-events"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url('${process.env.PUBLIC_URL}/Historic-Molly-Butler-Lodge-And-Restaurant-Greer-AZ.jpg')`
        }}
      >
        <div className="hero-content">
          <h2 className="section-title" style={{ marginBottom: '0.5rem', color: 'white' }}>Molly Butler Lodge</h2>
          <p style={{ textAlign: 'center', marginBottom: '0.75rem', fontSize: '1.1rem', color: 'rgba(255,255,255,0.9)' }}>
            Est. 1910
          </p>
          <p style={{ textAlign: 'center', marginBottom: '3rem', fontSize: '1rem', color: 'rgba(255,255,255,0.85)', letterSpacing: '0.05em' }}>
            Dinner served nightly at 5 PM
          </p>

          {/* Show events only if they exist, no loading state */}
          {upcomingEvents.length > 0 && (
            <div className="upcoming-events-grid">
              {upcomingEvents.map(event => (
                <div
                  key={event.id}
                  className="upcoming-event-card"
                  onClick={(e) => handleEventClick(event, e)}
                >
                  {(event.images && event.images.length > 0 ? event.images[0] : (event.imageUrl || event.image)) && (
                    <div className="upcoming-event-image">
                      <img
                        src={event.images && event.images.length > 0 ? event.images[0] : (event.imageUrl || event.image)}
                        alt={event.title}
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className="upcoming-event-content">
                    <div className="upcoming-event-date">
                      {formatEventDate(event.date)}
                    </div>
                    <h3 className="upcoming-event-title">{event.title}</h3>
                    <div className="upcoming-event-time">{event.time}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section section-white">
        <div className="container">
          <h2 className="section-title">Welcome to Molly Butler Lodge</h2>
          <p className="section-content">
            Nestled in the beautiful White Mountains of Arizona, Molly Butler Lodge has been serving guests since 1910.
            Our historic lodge offers a warm, welcoming atmosphere where families and friends can gather to create lasting memories.
          </p>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="section section-white">
        <div className="container">
          <h2 className="section-title">Gallery</h2>
          <p className="section-content" style={{ textAlign: 'center', marginBottom: '2rem' }}>
            Explore our historic lodge, dining, and activities
          </p>
          <InteractiveGallery layout="grid" />
        </div>
      </section>

      {/* Food Section */}
      <section id="food" className="dining-section">
        <div className="container">
          <div className="dining-header">
            <h2 className="section-title">Dining</h2>
            <p className="section-content">
              Experience authentic mountain dining in our historic restaurant. We serve hearty meals made with fresh, local ingredients.
            </p>
          </div>
          
          <div className="dining-content">
            <div className="menu-section">
              <h3 className="menu-section-title">Our Menus</h3>
              <div className="menu-grid">
                <a
                  href={process.env.PUBLIC_URL + '/Molly Dinner Menu.pdf'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="menu-card"
                  style={{ padding: '2rem', textAlign: 'center' }}
                >
                  <h4 className="menu-card-title">Dinner Menu</h4>
                  <p className="menu-card-description">Full dinner selections and specialties</p>
                </a>
                <a
                  href={process.env.PUBLIC_URL + '/Molly Lunch Menu.pdf'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="menu-card"
                  style={{ padding: '2rem', textAlign: 'center' }}
                >
                  <h4 className="menu-card-title">Lunch Menu</h4>
                  <p className="menu-card-description">Lighter fare and lunch favorites</p>
                </a>
                <a
                  href={process.env.PUBLIC_URL + '/Molly Kids Menu.pdf'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="menu-card"
                  style={{ padding: '2rem', textAlign: 'center' }}
                >
                  <h4 className="menu-card-title">Kids Menu</h4>
                  <p className="menu-card-description">Child-friendly options</p>
                </a>
                <a
                  href={process.env.PUBLIC_URL + '/Molly Beverage Menu.pdf'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="menu-card"
                  style={{ padding: '2rem', textAlign: 'center' }}
                >
                  <h4 className="menu-card-title">Beverage Menu</h4>
                  <p className="menu-card-description">Drinks, cocktails, and bar selections</p>
                </a>
              </div>
              
              <div className="reservations-box">
                <h3>Reservations</h3>
                <p>We recommend making reservations</p>
                <p>
                  <strong>Call:</strong> <a href="tel:928-735-7226">(928) 735-7226</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section contact-section">
        <div className="container">
          <h2 className="section-title">Visit Us</h2>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Location & Phone */}
              <div className="contact-card" style={{ padding: '2rem', textAlign: 'center' }}>
                <h3 style={{ fontSize: '1.3rem', marginBottom: '1rem', color: '#8B4513' }}>Location</h3>
                <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>109 Main Street<br />Greer, AZ 85927</p>
                <p style={{ fontSize: '1.1rem' }}>
                  <strong>Phone:</strong>
                  <a href="tel:928-735-7226" style={{ marginLeft: '0.5rem' }}>(928) 735-7226</a>
                </p>
              </div>

              {/* Hours */}
              <div className="contact-card" style={{ padding: '2rem' }}>
                <h3 style={{ fontSize: '1.3rem', marginBottom: '1rem', color: '#8B4513', textAlign: 'center' }}>Hours</h3>
                <div style={{ fontSize: '1rem', lineHeight: '1.8' }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <p style={{ marginBottom: '0.5rem' }}><strong>Dinner:</strong> 5:00 PM every night</p>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <p style={{ marginBottom: '0.5rem' }}><strong>Lunch:</strong> 11:00 AM – 2:30 PM</p>
                    <p style={{ fontSize: '0.9rem', color: '#666', paddingLeft: '1rem' }}>Saturday through Tuesday</p>
                  </div>
                  <div>
                    <p style={{ marginBottom: '0.5rem' }}><strong>Bar Hours:</strong></p>
                    <p style={{ fontSize: '0.9rem', paddingLeft: '1rem', marginBottom: '0.3rem' }}>Wed – Fri: 4:00 PM till close</p>
                    <p style={{ fontSize: '0.9rem', paddingLeft: '1rem' }}>Sat – Tue: 11:00 AM till close</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Event Detail Modal */}
      {showEventModal && selectedEvent && (
        <div className="modal-overlay" onClick={closeEventModal}>
          <div className="modal-content event-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedEvent.title}</h2>
              <button className="modal-close" onClick={closeEventModal}>
                ×
              </button>
            </div>

            <div className="event-detail-body">
              {/* Event Images Gallery */}
              {(selectedEvent.images && selectedEvent.images.length > 0) ? (
                <div className="event-detail-images">
                  <div className="image-gallery" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '10px',
                    marginBottom: '20px'
                  }}>
                    {selectedEvent.images.map((imageUrl, index) => (
                      <div key={index} className="gallery-image">
                        <img
                          src={imageUrl}
                          alt={`${selectedEvent.title} ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '150px',
                            objectFit: 'cover',
                            borderRadius: '8px'
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (selectedEvent.imageUrl || selectedEvent.image) && (
                <div className="event-detail-image">
                  <img
                    src={selectedEvent.imageUrl || selectedEvent.image}
                    alt={selectedEvent.title}
                    style={{
                      width: '100%',
                      maxHeight: '300px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      marginBottom: '20px'
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              )}

              <div className="event-detail-info">
                <div className="event-detail-datetime">
                  <div className="event-detail-date">
                    <strong>Date:</strong> {formatFullEventDate(selectedEvent.date)}
                  </div>
                  <div className="event-detail-time">
                    <strong>Time:</strong> {selectedEvent.time}
                  </div>
                </div>

                <div className="event-detail-description">
                  <h3>About This Event</h3>
                  <p>{selectedEvent.description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;